import mapValues from 'lodash/mapValues';

import type { ImportSection, SheetContext, SheetRows } from './types';

const EXCEL_EPOCH_UTC = Date.UTC(1899, 11, 30);
const MS_PER_DAY = 86_400_000;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function cellAt(rows: SheetRows, row: number, col: number): unknown {
  return rows[row]?.[col] ?? null;
}

export function cellAddress(row: number, col: number): string {
  let letters = '';
  for (let n = col + 1; n > 0; n = Math.floor((n - 1) / 26)) {
    letters = String.fromCharCode(65 + ((n - 1) % 26)) + letters;
  }
  return `${letters}${row + 1}`;
}

export function textOf(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

export function numberOf(value: unknown): number | null {
  const number = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof number === 'number' && Number.isFinite(number) ? number : null;
}

export function positiveNumberOf(value: unknown): number | null {
  const number = numberOf(value);
  return number !== null && number > 0 ? number : null;
}

// The tool shows and edits whole dollars, so a fraction would sit unseen under every figure built on it.
export function priceOf(value: unknown): number | null {
  const number = positiveNumberOf(value);
  return number === null ? null : Math.round(number) || null;
}

export function isoDateOf(value: unknown): string | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString().slice(0, 10);
  }
  // A date cell without a date format arrives as its serial number.
  if (typeof value === 'number' && value > 0) {
    return new Date(EXCEL_EPOCH_UTC + Math.round(value) * MS_PER_DAY).toISOString().slice(0, 10);
  }
  const text = textOf(value);
  return text && ISO_DATE.test(text) ? text : null;
}

function matchesLabel(value: unknown, label: string): boolean {
  return textOf(value)?.toLowerCase() === label.toLowerCase();
}

interface FindOptions {
  fromRow?: number;
  toRow?: number;
  col?: number;
}

export function findLabel(rows: SheetRows, label: string, options: FindOptions = {}) {
  const { fromRow = 0, toRow = rows.length - 1, col } = options;

  for (let row = fromRow; row <= Math.min(toRow, rows.length - 1); row++) {
    const cells = rows[row] ?? [];
    const lastCol = col ?? cells.length - 1;
    for (let c = col ?? 0; c <= lastCol; c++) {
      if (matchesLabel(cells[c], label)) {
        return { row, col: c };
      }
    }
  }
  return null;
}

// Every column is required: a section read without one would load half its data without anyone noticing.
export function findImportColumns<Column extends string>(
  { rows, sheet, issues }: SheetContext,
  labels: Record<Column, string>,
  section: ImportSection,
  noun: string,
): Record<Column, number> | null {
  const headerCol = (label: string) => findLabel(rows, label, { toRow: 0 })?.col;
  const missing = Object.values<string>(labels).find((label) => headerCol(label) === undefined);
  if (missing) {
    issues.push({
      section,
      severity: 'error',
      message: `The "${sheet}" sheet has no "${missing}" column, so no ${noun} will load.`,
    });
    return null;
  }
  return mapValues(labels, headerCol);
}
